import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Spin, message } from "antd";
import { gql, useLazyQuery } from "@apollo/client";
import { QRCodeSVG } from "qrcode.react";
import { SharePageContainer, QRCodeBox } from "./style";
import CoachPreview from "../../../components/CoachPreview";

const COACH_FOR_VISITOR = gql`
  query coachForVisitor($_id: String!) {
    coachForVisitor(_id: $_id) {
      _id
      name
      profile_image
      socialLink
      about
      subscriberCount
      subscriptionStatus
      hasActiveSubscription
    }
  }
`;

const PUBLIC_CHALLENGES_BY_COACH = gql`
  query publicChallengesByCoach($_id: String!, $args: PaginationArgs) {
    publicChallengesByCoach(_id: $_id, args: $args) {
      challenges {
        _id
        numberOfDays
        title
        description
        publishedAt
        scheduledAt
      }
      count
      limit
      offset
      total
    }
  }
`;

export default function ShareCoach() {
  const [searchParams] = useSearchParams();
  const coachId = searchParams.get("coachId");
  const [coachData, setCoachData] = useState(null);
  const [challenges, setChallenges] = useState(null);
  const [loading, setLoading] = useState(false);
  const [getCoach, { data, loading: queryLoading }] =
    useLazyQuery(COACH_FOR_VISITOR);
  const [getChallenges, { data: challengesData }] = useLazyQuery(
    PUBLIC_CHALLENGES_BY_COACH
  );

  useEffect(() => {
    // 移除 body 的 padding，因为分享页面应该是全屏的
    const computedStyle = window.getComputedStyle(document.body);
    const originalPaddingTop = computedStyle.paddingTop;
    const originalPaddingLeft = computedStyle.paddingLeft;

    document.body.style.paddingTop = "0";
    document.body.style.paddingLeft = "0";

    // 清理函数：组件卸载时恢复原来的 padding
    return () => {
      document.body.style.paddingTop = originalPaddingTop;
      document.body.style.paddingLeft = originalPaddingLeft;
    };
  }, []);

  useEffect(() => {
    if (coachId) {
      setLoading(true);
      getCoach({
        variables: {
          _id: coachId,
        },
      });
    } else {
      message.error("Coach ID is required");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachId]);

  useEffect(() => {
    if (data?.coachForVisitor) {
      setCoachData(data.coachForVisitor);
      setLoading(false);
    }
  }, [data]);

  useEffect(() => {
    if (queryLoading !== undefined) {
      setLoading(queryLoading);
    }
  }, [queryLoading]);

  useEffect(() => {
    if (coachId) {
      getChallenges({
        variables: {
          _id: coachId,
          args: {
            limit: 10,
            offset: 0,
          },
        },
      });
      setChallenges(challengesData?.publicChallengesByCoach?.challenges);
    }
  }, [coachId, getChallenges, challengesData]);

  // Current page URL for QR code
  const currentPageUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (coachId) params.set("coachId", coachId);
    return `${window.location.origin}/ShareCoach?${params.toString()}`;
  }, [coachId]);

  // Deep link URL for React Native app
  // Format: myapp://challenge/:challengeId
  // You can customize this based on your app's deep link scheme
  const deepLink = `uvitacoach://coach/${coachId}`;
  const handleOpenInApp = () => {
    if (!coachId) {
      message.error("Coach ID is missing");
      return;
    }

    // Detect if we're on iOS or Android
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    // Universal link for iOS (if configured)
    // const universalLink = `https://yourdomain.com/challenge/${challengeId}`;

    // App store links (replace with your actual app IDs)
    const iosAppStoreLink = "https://apps.apple.com/app/1561648928";
    const androidPlayStoreLink =
      "https://play.google.com/store/apps/details?id=com.uvitacoach";
    // 复制deeplink到剪贴板
    navigator.clipboard.writeText(deepLink);
    message.success("Link copied to clipboard");

    // Try to open the app using deep link
    if (isIOS) {
      // For iOS, try universal link first, then fallback to custom scheme
      // window.location.href = universalLink;
      window.location.href = deepLink;

      // Fallback to App Store after a delay if app doesn't open
      setTimeout(() => {
        if (document.hasFocus()) {
          // App didn't open, redirect to App Store
          window.location.href = iosAppStoreLink;
        }
      }, 2500);
    } else if (isAndroid) {
      // For Android, use intent URL for better compatibility
      const intentLink = `intent://coach/${coachId}#Intent;scheme=uvitacoach;package=com.uvitacoach;end`;

      // Try intent link first
      window.location.href = intentLink;
      // Fallback to custom scheme
      setTimeout(() => {
        if (document.hasFocus()) {
          window.location.href = deepLink;
        }
      }, 500);

      // Final fallback to Play Store
      setTimeout(() => {
        if (document.hasFocus()) {
          window.location.href = androidPlayStoreLink;
        }
      }, 2500);
    } else {
      // Desktop or other platforms - just try the deep link
      window.location.href = deepLink;
    }
  };

  if (loading) {
    return (
      <SharePageContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <Spin size="large" />
        </div>
      </SharePageContainer>
    );
  }

  if (!coachData) {
    return (
      <SharePageContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <div>Coach not found</div>
        </div>
      </SharePageContainer>
    );
  }

  return (
    <SharePageContainer>
      <div className="share-page-wrapper">
        <CoachPreview
          coachData={coachData}
          challenges={challenges}
          onOpenInApp={handleOpenInApp}
        />
      </div>
      <QRCodeBox>
        <div className="qr-text">
          <span>Get the app by</span>
          <span>scanning the QR code</span>
        </div>
        <QRCodeSVG value={currentPageUrl} size={100} />
      </QRCodeBox>
    </SharePageContainer>
  );
}
